import React, { useState, useRef, useId } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuLoaderCircle, LuPlus, LuRotateCcw } from 'react-icons/lu';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { db, storage } from '../../config/firebaseConfig';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import useBudgetText from './budgetText';
import InvoiceFormFields from './parts/InvoiceFormFields';

const initialFormData = {
  GibNumarasi: '',
  TaslakNo: '',
  Senaryo: '',
  FaturaTipi: '',
  OlusturmaTarihi: '',
  OlusturmaZamani: '',
  BelgeTarihi: '',
  BelgeZamani: '',
  MusteriTcknVkn: '',
  MusteriUnvan: '',
  MusteriEtiketi: '',
  Tutar: '',
  ParaBirimi: 'TRY',
  ZarfUuid: '',
  ZarfDurumu: '',
  Ettn: '',
  Iskonto: '',
  FaturaDisiMasraflari: '',
  SatirSayisi: '',
  BirimFiyatMiktarTutari: '',
  OdenecekTutar: '',
  VergilerHaricTutar: '',
  VergilerDahilTutar: '',
  ÖdemeDurumu: 'Alındı',
  AktifDurum: 'Aktif',
};

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

/**
 * "Yeni Gelir Ekle" (Tailwind): the e-invoice form with optional attachments.
 * The save logic is the old one (amounts as numbers, Bütçe* summary fields,
 * files to Storage under budgets/income/<id>/).
 */
const AddIncomeModal = ({ isOpen, onClose, onSubmit }) => {
  const { t, language, label } = useBudgetText();
  const uid = useId();
  // Toasts and messages read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).filter(file => !selectedFiles.some(f => f.name === file.name && f.size === file.size));
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };
  const handleFileInputChange = (e) => { handleFileSelect(e.target.files); e.target.value = ''; };
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files); };
  const removeFile = (index) => setSelectedFiles(prev => prev.filter((_, i) => i !== index));

  const uploadFiles = async (docId) => {
    const uploaded = [];
    for (const file of selectedFiles) {
      const storageRef = ref(storage, `budgets/income/${docId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      uploaded.push({ name: file.name, url, size: file.size });
    }
    return uploaded;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setSelectedFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const unvan = String(formData.MusteriUnvan || '').trim();
    const gib = String(formData.GibNumarasi || '').trim();
    const tutar = toNum(formData.Tutar);
    const belgeTarihi = formData.BelgeTarihi || formData.OlusturmaTarihi || '';

    const dataToSubmit = {
      ...formData,
      Tutar: tutar,
      Iskonto: toNum(formData.Iskonto),
      FaturaDisiMasraflari: toNum(formData.FaturaDisiMasraflari),
      SatirSayisi: toNum(formData.SatirSayisi),
      BirimFiyatMiktarTutari: toNum(formData.BirimFiyatMiktarTutari),
      OdenecekTutar: toNum(formData.OdenecekTutar),
      VergilerHaricTutar: toNum(formData.VergilerHaricTutar),
      VergilerDahilTutar: toNum(formData.VergilerDahilTutar),
      BütçeAdı: gib ? `${unvan} - ${gib}` : unvan || 'Gelir',
      BütçeKaynak: unvan,
      BütçeSahibi: 'Şirket',
      BütçeTipi: 'Gelir',
      GiderTipi: 'Gelir',
      BütçeKategorisi: 'E-Fatura',
      KDV: '0',
      BütçeMiktarı: tutar,
      BütçeTarihi: belgeTarihi,
      BeklenenÖdemeTarihi: belgeTarihi,
      createdAt: new Date().toISOString(),
      importSource: 'manual',
    };

    try {
      const docRef = await addDoc(collection(db, 'Budgets'), dataToSubmit);
      if (selectedFiles.length > 0) {
        const fileURLs = await uploadFiles(docRef.id);
        await updateDoc(doc(db, 'Budgets', docRef.id), { attachments: fileURLs });
        dataToSubmit.attachments = fileURLs;
      }
      toast.success(msg('incomeAdded'));
      handleReset();
      onSubmit && onSubmit(dataToSubmit);
      onClose();
    } catch (error) {
      console.error('Error adding income:', error);
      toast.error(msg('incomeAddError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formId = `${uid}-form`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={label('form.titleAddIncome')}
      subtitle={label('form.subtitleAdd')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-emerald-500/30" size="xs">
          <LuPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" strokeWidth={2.5} />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} className="sm:tw-mr-auto">{label('common.cancel')}</Button>
          <Button onClick={handleReset}>
            <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('form.reset')}
          </Button>
          <Button variant="primary" type="submit" form={formId} disabled={isSubmitting} className="max-sm:tw-flex-1">
            {isSubmitting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={3} />}
            {isSubmitting ? label('form.adding') : label('form.addIncome')}
          </Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit}>
        <InvoiceFormFields
          party="Musteri"
          idPrefix={uid}
          formData={formData}
          onChange={handleChange}
          statusOptions={['Alındı', 'Beklemede']}
          files={{
            selectedFiles,
            isDragging,
            inputRef: fileInputRef,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
            onInputChange: handleFileInputChange,
            onRemove: removeFile,
          }}
        />
      </form>
    </Modal>
  );
};

AddIncomeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};

export default AddIncomeModal;
